import { Alignment, DOMUtil, Navigation, ObjectUtil, Padding, PaddingResolver, StringUtil, WindowTitle } from "@mcleod/core";
import { DecoratorFactory, Panel, PanelProps, PanelStaticError } from "../..";
import { BehaviorTypes } from "../../base/BehaviorTypes";
import { Component } from "../../base/Component";
import { CrudDecoratorBehavior } from "./CrudDecoratorBehavior";
import { CrudDecoratorProps } from "./CrudDecoratorProps";
import { DataHeader } from "../../components/dataheader/DataHeader";
import { DataHeaderProps } from "../../components/dataheader/DataHeaderProps";
import { DataSourceMode } from "../../databinding/DataSource";
import { Decorator } from "./Decorator";
import { DecoratorAccessTester } from "./DecoratorAccessTester";
import { Label } from "../../components/label/Label";
import { MainPageAccessor } from "../../base/MainPageAccessor";
import { OverlayProps } from "../OverlayProps";
import { TransitionOptions } from "../../base/TransitionOptions";

export class CrudDecorator extends Decorator implements CrudDecoratorProps {
    private static _crudDecoratorBehavior: CrudDecoratorBehavior;
    public key: string;
    providedApiData: any | (() => any);
    private dataHeader: DataHeader;
    public mode: DataSourceMode;
    private _headerProps: Partial<DataHeaderProps>;
    private _overlayWrapper: Panel;
    private additionalSaveButtonLabels: Label[] = [];
    private _doAfterSlideIn: (decorator: CrudDecorator) => void;
    private _doAfterSlideOut: (decorator: CrudDecorator) => void;
    pseudoNavigateUrl: string;
    private _navigateBackAfterSlideOut: boolean;
    private _slideoutPromise: Promise<any>;
    private _slideInFromRight: boolean;

    constructor(props: Partial<CrudDecoratorProps>) {
        super({ id: "crudDecorator", ...props });
        this.fillHeight = true;
        if (this.pseudoNavigateUrl != null) {
            Navigation.pseudoNavigateTo(this.pseudoNavigateUrl, () => {
                this._navigateBackAfterSlideOut = false;
                this.slideOut();
            }).then(() => this._navigateBackAfterSlideOut = true);
        }
    }

    protected override handleLayoutLoadError(): void {
        // Let the CrudDecorator be displayed...the decorator's layout will display the error to the user via its
        // loadError member (and the PanelStaticError it loads/displays).  But do clear out the doAfterSlideIn and
        // doAfterSlideOut values so that no data-dependent actions take place.
        this.doAfterSlideIn = null;
        this.doAfterSlideOut = null;
    }

    override layoutLoaded() {
        if (this.layout.loadError == null) {
            const decoratorType = DecoratorFactory.resolveDecoratorType(this.mode);
            this.accessTestResult = DecoratorAccessTester.isDecoratorAllowed(this.layout, decoratorType);
            if (this.accessTestResult.allowed === false) {
                this.layout.loadError = this.accessTestResult.message;
            }
        }
        if (this.layout?.panelStaticError != null) {
            return;
        }
        this._setupDataHeader();
        this.layout.doAfterDataHeaderSetup(this.dataHeader);
        const dataSource = this.layout.mainDataSource;
        this.dataHeader.dataSource = dataSource;
        this.dataHeader.setSaveButtonAddlActions(this.additionalSaveButtonLabels);
        WindowTitle.push(() => this.dataHeader.layout.windowTitle);
        if (dataSource != null) {
            const layoutProvidedProps = this.layout.crudDecoratorProps;
            const validationMessage = layoutProvidedProps?.addlAccessCallback?.(this.key, this.mode);
            if (!StringUtil.isEmptyString(validationMessage)) {
                this.removeAll();
                this.layout.removeAll();
                this.add(new PanelStaticError(validationMessage));
                return;
            }
            if (this.providedApiData == null || layoutProvidedProps.customDataLoader == null) {
                if (this.key != null && this.mode === DataSourceMode.UPDATE) {
                    const md = dataSource.getMetadataFromCache();
                    const keyField = md?.keyFields?.[0] || "id";
                    dataSource.search({ [keyField]: this.key }).then(result => {
                        this.layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
                    });
                }
                else if (this.mode != null) {
                    this.layout.setAllDataSourcesToMode(this.mode);
                }
            }
            else
                layoutProvidedProps.customDataLoader(this.providedApiData, this.mode);
        }
    }

    private _setupDataHeader() {
        if (this.layout.dataHeader == null) {
            this.dataHeader = new DataHeader({ layout: this.layout });
            //need to insert DataHeader as quickly as possible so that it can update key listeners as it is built (needs to be tied back to its parent panel)
            this.crudDecoratorBehavior.insertDataHeader(this, this.dataHeader);
            this.dataHeader.setProps(this.headerProps);
            this.dataHeader.setSaveButtonAddlActions(this.additionalSaveButtonLabels);
        } else {
            this.dataHeader = this.layout.dataHeader;
            //need to insert DataHeader as quickly as possible so that it can update key listeners as it is built (needs to be tied back to its parent panel)
            this.dataHeader.layout = this.layout;
            this.crudDecoratorBehavior.insertDataHeader(this, this.dataHeader);
            if (ObjectUtil.isEmptyObject(this.headerProps) !== true)
                this.dataHeader.setProps(this.headerProps);
        }
        this.syncDataHeader();
    }

    private syncDataHeader() {
        if (this._slideInFromRight && this.dataHeader != null) {
            this.layout.titleForModeOverrideFunction = (title, mode) => {
                switch (mode) {
                    case DataSourceMode.ADD:
                        return "Add " + title;
                    case DataSourceMode.UPDATE:
                        return "Edit " + title;
                    default:
                        return title;
                }
            };
            this.dataHeader.labelTitleFontSize = "large";
            this.dataHeader.backgroundColor = "subtle.lightest";
        }
    }

    get headerProps(): Partial<DataHeaderProps> {
        return this._headerProps;
    }

    public set headerProps(value: Partial<DataHeaderProps>) {
        this._headerProps = value;
        this.dataHeader?.setProps(value);
        this.dataHeader?.setSaveButtonAddlActions(this.additionalSaveButtonLabels);
    }

    /**
     * If props didn't specify where the user should be navigated after an action is completed, then assume we should
     * slide the CrudDecorator out.
     */
    private defaultDataHeaderNavigateOnClose() {
        this.layout.addLayoutLoadListener(() => {
            if (this.dataHeader != null && this.dataHeader.navigateOnClose == null)
                this.dataHeader.navigateOnClose = () => this.slideOut();
        });
    }

    get doAfterSlideIn(): (decorator: CrudDecorator) => void {
        return this._doAfterSlideIn;
    }

    set doAfterSlideIn(value: (decorator: CrudDecorator) => void) {
        this._doAfterSlideIn = value;
    }

    get doAfterSlideOut(): (decorator: CrudDecorator) => void {
        return this._doAfterSlideOut;
    }

    set doAfterSlideOut(value: (decorator: CrudDecorator) => void) {
        this._doAfterSlideOut = value;
    }

    private setErrorPanelCloseCallback() {
        if (this.layout.panelStaticError != null) {
            this.layout.panelStaticError.closeCallback = () => this.slideOut()
        }
    }

    public override async slideIn(options?: TransitionOptions, displayOverlay?: boolean,
        overlayProps?: Partial<OverlayProps>, paddingOverride?: Partial<Padding>): Promise<any> {
        if (this.layout.panelStaticError != null) {
            this.setErrorPanelCloseCallback();
        } else {
            this.layout.addLoadErrorListener(() => this.setErrorPanelCloseCallback());
        }
        this._slideoutPromise = null;
        options = { speed: 200, direction: Alignment.BOTTOM, ...options };
        this._slideInFromRight = options?.direction == Alignment.RIGHT;
        this.syncDataHeader();

        displayOverlay ??= true;
        overlayProps = {
            closeOnClickOff: false,
            coverPageHeader: this._slideInFromRight === false ? false : undefined,
            greyedBackground: this._slideInFromRight === true ? true : undefined,
            ...overlayProps
        };
        paddingOverride = { ...paddingOverride };

        this._createOverlayWrapper(paddingOverride);
        this.defaultDataHeaderNavigateOnClose();

        return this._overlayWrapper.slideIn(options, displayOverlay, overlayProps).then(() => {
            if (this.doAfterSlideIn != null)
                this.doAfterSlideIn(this);
        });

    }

    public override async slideOut(options?: TransitionOptions, componentToFocusOnClose?: Component): Promise<any> {
        if (this._slideoutPromise != null)
            return this._slideoutPromise;
        this._slideoutPromise = this._overlayWrapper?.slideOut(options, componentToFocusOnClose).then(() => {
            this.internalDoAfterSlideOut();
            return this;
        });
        return this._slideoutPromise;
    }

    private internalDoAfterSlideOut() {
        //remove the CrudDecorator from the overlay wrapper in case we slide it in again (which would be in a new wrapper)
        this._overlayWrapper.remove(this);
        WindowTitle.pop();
        if (this.doAfterSlideOut != null)
            this.doAfterSlideOut(this);

        if (this._navigateBackAfterSlideOut === true) {
            this._navigateBackAfterSlideOut = false;
            Navigation.navigateBack();
        }
    }

    private _createOverlayWrapper(paddingOverride?: Partial<Padding>) {
        this._overlayWrapper = new Panel({
            id: "overlay-wrapper",
            padding: 0,
            backgroundColor: "backgroundSubtle",
            borderColor: "strokeSecondary",
            borderRadius: 4,
            borderWidth: 1,
            borderShadow: true,
            scrollX: true,
            scrollY: true,
            ...this.getOverlayWrapperProps(paddingOverride)
        });
        this._overlayWrapper.add(this);
    }

    private getOverlayWrapperProps(paddingOverride?: Partial<Padding>): Partial<PanelProps> {
        if (this._slideInFromRight) {
            return {
                width: this.layout.width,
                height: "calc(100% - " + MainPageAccessor.pageHeaderHeight + "px)",
            };
        } else {
            const paddingOverrideLeft = PaddingResolver.getPaddingLeft(paddingOverride);
            const routerPanelPaddingLeft = paddingOverrideLeft == null ? DOMUtil.getComputedSize("padding-left", MainPageAccessor.router._element) : paddingOverrideLeft;
            const paddingOverrideRight = PaddingResolver.getPaddingRight(paddingOverride);
            const routerPanelPaddingRight = paddingOverrideRight == null ? DOMUtil.getComputedSize("padding-right", MainPageAccessor.router._element) : paddingOverrideRight;
            const paddingOverrideTop = PaddingResolver.getPaddingTop(paddingOverride);
            const routerPanelPaddingTop = paddingOverrideTop == null ? DOMUtil.getComputedSize("padding-top", MainPageAccessor.router._element) : paddingOverrideTop;
            const widthToSubtract = routerPanelPaddingLeft + routerPanelPaddingRight;
            return {
                width: "calc(100% - " + widthToSubtract + "px)",
                height: "calc(100% - " + routerPanelPaddingTop + "px)",
                marginLeft: routerPanelPaddingLeft,
                marginRight: routerPanelPaddingRight
            };
        }
    }

    public addOptionToSaveButton(text: string, onClick: () => void) {
        this.additionalSaveButtonLabels.push(new Label({ text: text, onClick: onClick }));
        this.dataHeader?.setSaveButtonAddlActions(this.additionalSaveButtonLabels);
    }

    private get crudDecoratorBehavior(): CrudDecoratorBehavior {
        CrudDecorator._crudDecoratorBehavior ??= BehaviorTypes.get("CrudDecorator");
        return CrudDecorator._crudDecoratorBehavior;
    }

    override displayInViewMode() {
        super.displayInViewMode();
        if (this.accessTestResult?.displaysInViewMode === true && this.dataHeader != null) {
            this.dataHeader.displayInViewMode();
        }
    }
}
